import React from "react";
import { ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from "recharts";
import { Info, Lock } from "lucide-react";
import { isUnavailable, moduleLabel, unportedModules } from "@/pages/command/unavailable";

/*
 * Command Center shared components (R-016).
 *
 * The rule these all encode: a metric the API could not compute is rendered as
 * an explicit "Not yet available", never as a digit, a bar, or a chart segment.
 * See ./unavailable.js for why that distinction is the point of the module.
 */

/** The inline marker for a value with no source module yet. */
export function Unavailable({ module, compact = false }) {
  const label = module ? `Needs ${moduleLabel(module)}` : "Not yet available";

  return (
    <span
      className={`cc-unavailable ${compact ? "compact" : ""}`}
      title={module ? `${moduleLabel(module)} is not part of the API yet.` : "No source module for this metric yet."}
      data-testid="cc-unavailable"
    >
      <Lock className="w-3 h-3" aria-hidden="true" />
      {compact ? "—" : label}
    </span>
  );
}

/**
 * A KPI tile.
 *
 * `value == null` means the API could not compute it, so the tile shows the
 * unavailable marker and stops being clickable — drilling into a number that
 * does not exist leads nowhere useful.
 */
export function StatTile({ icon: Icon, accent = "#0B7E8C", label, value, spark, onClick, testId, caption, module }) {
  const missing = isUnavailable(value);
  const clickable = onClick && !missing;

  return (
    <button
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`cc-tile ${clickable ? "clickable" : ""} ${missing ? "muted" : ""}`}
      data-testid={testId}
    >
      <span className="cc-tile-icon" style={{ background: `${accent}15`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="cc-tile-label">{label}</div>
        <div className="cc-tile-value">
          {missing ? <Unavailable module={module} /> : value}
        </div>
        {caption && !missing && (
          <div className="cc-tile-caption" data-testid={testId ? `${testId}-caption` : undefined}>
            {caption}
          </div>
        )}
        {spark && !missing && (
          <div className="cc-sparkline">
            <ResponsiveContainer width="100%" height={28}>
              <AreaChart data={spark}>
                <defs>
                  <linearGradient id={`grad-${label}`} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={accent} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={1.5} fill={`url(#grad-${label})`} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * Stands in for a chart whose source module is not ported.
 *
 * Deliberately not an empty chart: an axis with no bars reads as "no patients
 * match", which is a factual claim this API cannot currently make.
 */
export function ChartUnavailable({ module, what, height = 200 }) {
  return (
    <div className="cc-chart-empty" style={{ minHeight: height }} data-testid="cc-chart-unavailable">
      <Info className="w-5 h-5" aria-hidden="true" />
      <div className="cc-chart-empty-title">{what} is not available yet</div>
      <div className="cc-chart-empty-sub">
        Computed from the {moduleLabel(module)} module, which is not part of the
        API yet. Nothing is charted rather than a misleading zero.
      </div>
    </div>
  );
}

/**
 * Page-level banner listing every module a surface is missing.
 *
 * Shown once at the top so a reader knows the shape of what they are looking at
 * before they read any individual figure.
 */
export function SourceBanner({ sources }) {
  const missing = unportedModules(sources);
  if (missing.length === 0) return null;

  return (
    <div className="cc-source-banner" role="status" data-testid="cc-source-banner">
      <Info className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span>
        <strong>Partial data.</strong> Metrics from <strong>{missing.join(", ")}</strong>{" "}
        {missing.length === 1 ? "is" : "are"} not available yet, and appear as
        “Not yet available” rather than zero.
      </span>
    </div>
  );
}

/** A risk-distribution tile. Renders the unavailable marker when the count is null. */
export function RiskTile({ icon: Icon, label, value, pctVal, accent, bg, module }) {
  const missing = isUnavailable(value);

  return (
    <div className={`cc-risk-tile ${missing ? "muted" : ""}`}>
      <span className="cc-risk-tile-icon" style={{ background: bg, color: accent }}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="flex-1">
        <div className="cc-risk-tile-label">{label}</div>
        <div className="cc-risk-tile-value-row">
          {missing ? (
            <Unavailable module={module} />
          ) : (
            <>
              <span className="cc-risk-tile-value">{value}</span>
              {pctVal !== null && (
                <span className="cc-risk-tile-pct" style={{ color: accent }}>{pctVal}%</span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** A small donut. Returns the unavailable panel rather than a 0% ring. */
export function DonutMini({ value, label, tone = "good", module }) {
  if (isUnavailable(value)) {
    return (
      <div className="text-center">
        <div className="cc-donut-empty"><Lock className="w-4 h-4" aria-hidden="true" /></div>
        <div className="cc-donut-label">{label}</div>
        <div className="cc-donut-missing">{module ? `Needs ${moduleLabel(module)}` : "No data"}</div>
      </div>
    );
  }

  const color = tone === "warn" ? "#F59E0B" : tone === "bad" ? "#EF4444" : "#0B7E8C";

  return (
    <div className="text-center">
      <div className="relative inline-block">
        <PieChart width={72} height={72}>
          <Pie
            data={[{ v: value }, { v: 100 - value }]}
            dataKey="v" cx={36} cy={36} innerRadius={22} outerRadius={32}
            startAngle={90} endAngle={-270} stroke="none"
          >
            <Cell fill={color} /><Cell fill="#F1F5F9" />
          </Pie>
        </PieChart>
        <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-[#1F2937]">
          {Math.round(value)}%
        </div>
      </div>
      <div className="cc-donut-label">{label}</div>
    </div>
  );
}

/**
 * Head-count card.
 *
 * The prototype's utilisation bar is gone: its 67 / 81 / 72 / 65 percentages
 * were hard-coded literals with no data behind them (R-016). Real utilisation
 * needs shift rosters and assigned workload from fleet/care_teams, and the API
 * reports `utilisation_available: false` until then.
 */
export function UtilCard({ icon: Icon, accent, bg, label, count }) {
  return (
    <div className="cc-card flex items-center gap-4">
      <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: bg, color: accent }}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="flex-1">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-[#6B7280] font-medium">{label}</span>
          <span className="text-2xl font-bold text-[#111827]">
            {isUnavailable(count) ? <Unavailable compact /> : count}
          </span>
        </div>
        <div className="cc-tile-caption mt-1">Active staff in this role</div>
      </div>
    </div>
  );
}

/** A labelled figure that may be unavailable — used in dense stat grids. */
export function Figure({ value, module, className = "" }) {
  return isUnavailable(value)
    ? <Unavailable module={module} compact />
    : <span className={className}>{value}</span>;
}

/** Relative timestamp for feed rows. */
export function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/** Shared loading state. */
export function Loading() {
  return <div className="p-6 text-sm text-[#6B7280]">Loading…</div>;
}
